import {
  FacebookBookingRequestEvent,
  FacebookCustomEvent,
  FacebookInitiateCheckoutEvent,
  FacebookPurchaseEvent,
} from './facebook';
import {
  FirebaseBeginCheckoutEvent,
  FirebaseBookingRequestEvent,
  FirebaseOrderItem,
  FirebasePurchaseEvent,
} from './firebase';
import { logError } from '../util/log';
import { bookingPurchaseParams, clientPlatform, isPassBooking, passBookingParams } from './purchaseTracking';
import {
  EXTRA_LINE_ITEMS,
  LINE_ITEMS,
  LINE_ITEM_DAY,
  LINE_ITEM_NIGHT,
  LINE_ITEM_UNITS,
} from '../util/types';

// Fallback name for the stay line item when no listing title can be resolved.
export const FALLBACK_LISTING_ITEM_NAME = 'Stellplatz-Buchung';

// Only the actual stay positions carry the listing name — LINE_ITEMS also
// contains commissions/vouchers, which must keep falling back to their item_id.
const STAY_LINE_ITEMS = [LINE_ITEM_NIGHT, LINE_ITEM_DAY, LINE_ITEM_UNITS];

const createGoogleItems = (lineItems, processName, processVersion, listingTitle) => {
  return lineItems.reduce((acc, lineItem) => {
    if (lineItem.includeFor.includes('customer')) {
      const isStayLineItem = STAY_LINE_ITEMS.includes(lineItem.code);
      acc.push(
        FirebaseOrderItem.withItemId(lineItem.code)
          // Only the stay line item gets a readable name; extras and custom items
          // stay nameless so GA4 falls back to their item_id.
          .withItemName(isStayLineItem ? listingTitle || FALLBACK_LISTING_ITEM_NAME : null)
          .withPrice(lineItem.lineTotal.amount === 0 ? 0 : lineItem.lineTotal.amount / 100)
          .withQuantity(lineItem.quantity ? lineItem.quantity : 1)
          .withCurrency(lineItem.lineTotal.currency.toLowerCase())
          .withItemCategory(
            LINE_ITEMS.includes(lineItem.code)
              ? 'line_item'
              : EXTRA_LINE_ITEMS.includes(lineItem.code)
                ? 'extra_line_item'
                : 'custom_line_item',
          )
          .withItemCategory2(processName)
          .withItemCategory3(processVersion)
          .make(),
      );
    }
    return acc;
  }, []);
};

const createFacebookItems = (lineItems) => {
  const items = lineItems.reduce((acc, lineItem) => {
    if (lineItem.includeFor.includes('customer')) {
      acc.push({
        id: lineItem.code,
        price: lineItem.lineTotal.amount === 0 ? 0 : lineItem.lineTotal.amount / 100,
        quantity: lineItem.quantity ? lineItem.quantity : 1,
        currency: lineItem.lineTotal.currency.toLowerCase(),
        category: LINE_ITEMS.includes(lineItem.code)
          ? 'line_item'
          : EXTRA_LINE_ITEMS.includes(lineItem.code)
            ? 'extra_line_item'
            : 'custom_line_item',
      });
    }
    return acc;
  }, []);
  // Wie der Server (buildFacebookContents): Meta bekommt keine Positionen mit Preis <= 0
  // (0-€-Extras, Pass-Rabatt), damit num_items auf beiden Seiten gleich bleibt.
  return items.filter((item) => item.price > 0);
};

// Single-listing variant of createGoogleItems for events that carry a bare
// listing instead of transaction line items (view_item, add_to_wishlist).
export const createListingItem = (listing) => {
  const price = listing.attributes.price;
  const category = listing.attributes.publicData?.category;
  const item = FirebaseOrderItem.withItemId(listing.id.uuid)
    .withItemName(listing.attributes.title)
    .withPrice(price.amount === 0 ? 0 : price.amount / 100)
    .withQuantity(1)
    .withCurrency(price.currency.toLowerCase())
    .withItemCategory(category)
    .make();
  // Listings without publicData.category must omit item_category entirely
  // rather than send an empty string.
  if (!item.item_category) delete item.item_category;
  return [item];
};

// Pass-Buchungen gehen nur als Meta-Custom-Event raus: GA4 bekommt `pass_booking` allein vom
// Server (ga4.js mappt PassBooking auf null), sonst würde GA4 doppelt zählen.
const trackPassBooking = (transaction, currentUser, contents) => {
  FacebookCustomEvent.withCurrentUserUUID(currentUser?.id?.uuid)
    .withCustomName('PassBooking')
    .withEventID(transaction.id.uuid)
    .withParams({
      ...passBookingParams(transaction, clientPlatform()),
      contents,
      num_items: contents.length,
    })
    .logEvent();
};

const trackBookingPurchase = (transaction, currentUser, listingTitle, contents) => {
  const { lineItems, processName, processVersion, metadata } = transaction.attributes;
  // Alles außer value/currency (tax, purchase_type, product, platform, booking_volume,
  // pass_discount) läuft als Zusatzparameter mit — identisch zum Server.
  const { value, currency, ...extraParams } = bookingPurchaseParams(transaction, clientPlatform());
  FirebasePurchaseEvent.withTransactionId(transaction.id.uuid)
    .withCurrency(currency)
    .withValue(value)
    .withCoupon(metadata?.voucher?.code)
    .withItems(
      createGoogleItems(
        lineItems,
        processName,
        processVersion,
        listingTitle || transaction.listing?.attributes?.title,
      ),
    )
    .withAdditionalKeys(Object.entries(extraParams).map(([key, v]) => ({ key, value: v })))
    .logEvent();
  FacebookPurchaseEvent.withCurrentUserUUID(currentUser?.id?.uuid)
    .withEventID(transaction.id.uuid)
    .withValue(value)
    .withCurrency(currency)
    .withContents(contents)
    .withNumItems(contents.length)
    .withParams({ ...extraParams, order_id: transaction.id.uuid })
    .logEvent();
};

export const trackPurchase = (transaction, currentUser, listingTitle) => {
  try {
    const contents = createFacebookItems(transaction.attributes.lineItems);
    if (isPassBooking(transaction)) {
      trackPassBooking(transaction, currentUser, contents);
    } else {
      trackBookingPurchase(transaction, currentUser, listingTitle, contents);
    }
  } catch (e) {
    logError(e, 'track-purchase-failed');
  }
};

export const trackInitPurchase = (transaction, orderParams, currentUser, listingTitle) => {
  try {
    const { lineItems, processName, processVersion, payinTotal } = transaction.attributes;
    const googleItems = createGoogleItems(
      lineItems,
      processName,
      processVersion,
      listingTitle || transaction.listing?.attributes?.title,
    );
    FirebaseBeginCheckoutEvent.withValue(payinTotal.amount > 0 ? payinTotal.amount / 100 : 0)
      .withCurrency(payinTotal.currency.toLowerCase())
      .withCoupon(orderParams.bookingData?.voucher?.code)
      .withItems(googleItems)
      .logEvent();
    const facebookItems = createFacebookItems(lineItems);
    FacebookInitiateCheckoutEvent.withCurrentUserUUID(currentUser?.id?.uuid)
      .withValue(payinTotal.amount > 0 ? payinTotal.amount / 100 : 0)
      .withCurrency(payinTotal.currency.toLowerCase())
      .withContents(facebookItems)
      .logEvent();
  } catch (e) {
    logError(e, 'track-init-purchase-failed');
  }
};

const PURCHASE_TRACKED_PREFIX = 'purchase_tracked_';
const BEGIN_CHECKOUT_TRACKED_PREFIX = 'begin_checkout_tracked_';

// true nur beim ersten Aufruf je key in dieser Browser-Session. Ohne
// sessionStorage (Safari private mode) immer true — dann feuert das Event ohne Guard.
const isFirstInSession = (key) => {
  try {
    if (window.sessionStorage.getItem(key)) return false;
    window.sessionStorage.setItem(key, '1');
  } catch (e) {
    // kein sessionStorage — ohne Guard weiter
  }
  return true;
};

/**
 * Purchase mit Dedup-Guard für die OrderDetailsPage: Router-State übersteht
 * keinen Reload, sessionStorage fängt Remounts/Zurück-Navigation in derselben
 * Session ab. eventID/transaction_id bleibt die TX-UUID (Plattform-Dedup).
 */
export const trackPurchaseOnce = (transaction, currentUser) => {
  if (typeof window === 'undefined') return;
  if (!isFirstInSession(`${PURCHASE_TRACKED_PREFIX}${transaction.id.uuid}`)) return;
  trackPurchase(transaction, currentUser);
};

/**
 * begin_checkout/InitiateCheckout einmal pro Listing und Session: Gäste-, Extras-
 * und Pass-Änderungen rechnen dieselbe Checkout-Spekulation neu, sind aber kein
 * neuer Checkout-Start.
 */
export const trackInitPurchaseOnce = (transaction, orderParams, currentUser, listingTitle) => {
  if (typeof window === 'undefined') return;
  const key = `${BEGIN_CHECKOUT_TRACKED_PREFIX}${orderParams.listingId?.uuid}`;
  if (!isFirstInSession(key)) return;
  trackInitPurchase(transaction, orderParams, currentUser, listingTitle);
};

export const trackBookingRequest = (transaction, currentUser, listingTitle) => {
  try {
    const { lineItems, processName, processVersion, payinTotal, metadata } = transaction.attributes;
    const value = payinTotal.amount / 100;
    const currency = payinTotal.currency.toLowerCase();
    FirebaseBookingRequestEvent.withTransactionId(transaction.id.uuid)
      .withValue(value)
      .withCurrency(currency)
      .withCoupon(metadata?.voucher?.code)
      .withItems(
        createGoogleItems(
          lineItems,
          processName,
          processVersion,
          listingTitle || transaction.listing?.attributes?.title,
        ),
      )
      .logEvent();
    const contents = createFacebookItems(lineItems);
    FacebookBookingRequestEvent.withCurrentUserUUID(currentUser?.id?.uuid)
      .withEventID(transaction.id.uuid)
      .withValue(value)
      .withCurrency(currency)
      .withContents(contents)
      .withNumItems(contents.length)
      .logEvent();
  } catch (e) {
    logError(e, 'track-booking-request-failed');
  }
};
